<template>
  <el-dialog
    v-model="visible"
    title=""
    width="480px"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    destroy-on-close
    class="login-dialog"
  >
    <div class="auth-container">
      <div class="auth-header">
        <el-icon :size="40" color="var(--primary-color)"><Reading /></el-icon>
        <h2>{{ isLogin ? '欢迎回来' : '注册账号' }}</h2>
        <p>{{ isLogin ? '登录以继续您的学习之旅' : '创建账号开启学习之旅' }}</p>
      </div>

      <!-- 登录模式 -->
      <template v-if="isLogin">
        <div class="login-methods">
          <div
            v-for="method in loginMethods"
            :key="method.key"
            class="method-tab"
            :class="{ active: currentLoginMethod === method.key }"
            @click="currentLoginMethod = method.key"
          >
            <el-icon :size="18">
              <component :is="method.icon" />
            </el-icon>
            <span>{{ method.label }}</span>
          </div>
        </div>

        <div class="login-form-wrapper">
          <PasswordLoginForm
            v-if="currentLoginMethod === 'password'"
            @success="onLoginSuccess"
            @forgot-password="showForgotPassword"
          />
          <SmsLoginForm
            v-else-if="currentLoginMethod === 'sms'"
            :phone="switchToLoginPhone"
            @success="onLoginSuccess"
          />
          <div v-else-if="currentLoginMethod === 'wechat'" class="wechat-placeholder">
            <el-icon :size="48"><ChatDotRound /></el-icon>
            <p>暂不支持，请使用密码或手机验证码登录</p>
          </div>
        </div>

        <div class="login-options">
          <span>还没有账号？</span>
          <el-button link type="primary" @click="switchMode('register')">
            立即注册
          </el-button>
        </div>
      </template>

      <!-- 注册模式 -->
      <template v-else>
        <RegisterForm @success="onRegisterSuccess" @switch-to-login="handleSwitchToLogin" />

        <div class="login-options">
          <span>已有账号？</span>
          <el-button link type="primary" @click="switchMode('login')">
            立即登录
          </el-button>
        </div>
      </template>
    </div>

    <!-- 忘记密码弹窗 -->
    <ForgotPasswordDialog
      v-model:visible="forgotPasswordVisible"
      @success="onResetSuccess"
    />
  </el-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue'
import { Reading, Iphone, Message, ChatDotRound } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import PasswordLoginForm from '@/views/auth/components/PasswordLoginForm.vue'
import SmsLoginForm from '@/views/auth/components/SmsLoginForm.vue'
import RegisterForm from '@/views/auth/components/RegisterForm.vue'
import ForgotPasswordDialog from '@/views/auth/components/ForgotPasswordDialog.vue'

type LoginMethod = 'password' | 'sms' | 'wechat'
type AuthMode = 'login' | 'register'

const props = defineProps<{
  modelValue: boolean
  mode?: AuthMode
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  success: []
}>()

const visible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

const currentLoginMethod = ref<LoginMethod>('password')
const currentMode = ref<AuthMode>(props.mode || 'login')
const forgotPasswordVisible = ref(false)
const switchToLoginPhone = ref('')

// 监听 props.mode 变化
watch(() => props.mode, (newMode) => {
  if (newMode) {
    currentMode.value = newMode
  }
})

const isLogin = computed(() => currentMode.value === 'login')

const loginMethods = [
  { key: 'password' as LoginMethod, label: '密码登录', icon: Iphone },
  { key: 'sms' as LoginMethod, label: '验证码登录', icon: Message },
  { key: 'wechat' as LoginMethod, label: '微信登录', icon: ChatDotRound },
]

const switchMode = (mode: AuthMode) => {
  currentMode.value = mode
}

const handleSwitchToLogin = (phone: string) => {
  switchToLoginPhone.value = phone
  currentLoginMethod.value = 'sms'
  nextTick(() => switchMode('login'))
}

const onLoginSuccess = () => {
  ElMessage.success({ message: '登录成功', duration: 3000 })
  visible.value = false
  emit('success')
}

const onRegisterSuccess = () => {
  ElMessage.success({ message: '注册成功', duration: 3000 })
  visible.value = false
  emit('success')
}

const showForgotPassword = () => {
  forgotPasswordVisible.value = true
}

const onResetSuccess = () => {
  ElMessage.success({ message: '密码重置成功，请使用新密码登录', duration: 3000 })
  forgotPasswordVisible.value = false
}
</script>

<style scoped lang="scss">
.login-dialog {
  :deep(.el-dialog__header) {
    display: none;
  }

  :deep(.el-dialog__body) {
    padding: 0;
  }
}

.auth-container {
  padding: 32px;
}

.auth-header {
  text-align: center;
  margin-bottom: 24px;

  h2 {
    font-size: 20px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 12px 0 6px;
  }

  p {
    font-size: 13px;
    color: var(--text-secondary);
  }
}

.login-methods {
  display: flex;
  border-bottom: 1px solid var(--border-light);
  margin-bottom: 20px;

  .method-tab {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 10px 0;
    cursor: pointer;
    color: var(--text-secondary);
    transition: all 0.2s;
    position: relative;

    &:hover {
      color: var(--primary-color);
    }

    &.active {
      color: var(--primary-color);
      font-weight: 500;

      &::after {
        content: '';
        position: absolute;
        bottom: -1px;
        left: 20%;
        right: 20%;
        height: 2px;
        background: var(--primary-color);
        border-radius: 2px;
      }
    }

    span {
      font-size: 12px;
    }
  }
}

.login-form-wrapper {
  min-height: 260px;
}

.login-options {
  text-align: center;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--border-light);
  font-size: 13px;
  color: var(--text-secondary);

  .el-button {
    font-size: 13px;
  }
}

.wechat-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: var(--text-placeholder);

  p {
    margin-top: 16px;
    font-size: 14px;
    color: var(--text-secondary);
  }
}
</style>
